import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import BillingNavbar from './BillingNavbar';
import BillingQuickActions from './BillingQuickActions';

const BillingLayout = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);
  const [showShortcutToast, setShowShortcutToast] = useState(null);

  const triggerNavigate = (path, shortcutLabel) => {
    navigate(path);
    setShowShortcutToast(shortcutLabel);
    setTimeout(() => setShowShortcutToast(null), 1500);
  };

  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase();
        const validKeys = ['b', 'i', 'p', 'd', 'r'];

        if (validKeys.includes(key)) {
          e.preventDefault();
          e.stopPropagation();

          switch (key) {
            case 'b': triggerNavigate('/billing/form', 'Ctrl+B → New Bill'); break;
            case 'i': triggerNavigate('/billing/list', 'Ctrl+I → Bill List'); break;
            case 'p': triggerNavigate('/billing/payment', 'Ctrl+P → Payment'); break;
            case 'd': triggerNavigate('/billing/due-list', 'Ctrl+D → Due Collection'); break;
            case 'r': triggerNavigate('/billing/summary', 'Ctrl+R → Daily Summary'); break;
            default: break;
          }
        }
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown, true);
  }, [navigate]);

  return (
    <div className="billing-layout">
      <BillingNavbar />

      <div className="billing-main-area">
        <div className="billing-content-wrapper">
          <div className="billing-inner-canvas">
            <Outlet />
          </div>
        </div>

        <div className={`billing-sidebar-right d-print-none ${sidebarCollapsed ? 'collapsed' : ''}`}>
          <button
            className="sidebar-toggle-btn"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            title={sidebarCollapsed ? 'Expand Panel' : 'Collapse Panel'}
          >
            <i className={`bi ${sidebarCollapsed ? 'bi-chevron-left' : 'bi-chevron-right'}`}></i>
          </button>
          {!sidebarCollapsed && <BillingQuickActions />}
          {sidebarCollapsed && (
            <div className="collapsed-icons">
              <button className="collapsed-icon-btn" onClick={() => navigate('/billing/form')} title="New Bill (Ctrl+B)">
                <i className="bi bi-file-earmark-plus" style={{ color: '#d97706' }}></i>
              </button>
              <button className="collapsed-icon-btn" onClick={() => navigate('/billing/payment')} title="Payment (Ctrl+P)">
                <i className="bi bi-cash" style={{ color: '#16a34a' }}></i>
              </button>
              <button className="collapsed-icon-btn" onClick={() => navigate('/billing/list')} title="Bill List (Ctrl+I)">
                <i className="bi bi-list-ul" style={{ color: '#2563eb' }}></i>
              </button>
              <button className="collapsed-icon-btn" onClick={() => navigate('/billing/due-list')} title="Pending Dues (Ctrl+D)">
                <i className="bi bi-journal-x" style={{ color: '#dc2626' }}></i>
              </button>
              <button className="collapsed-icon-btn" onClick={() => navigate('/billing/summary')} title="Daily Summary (Ctrl+R)">
                <i className="bi bi-pie-chart" style={{ color: '#9333ea' }}></i>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer Status Bar */}
      <div className="billing-statusbar d-print-none">
        <div className="status-left">
          <span className="status-item">
            <span className="status-dot-live"></span>
            <span className="status-online">Online</span>
          </span>
          <span className="status-divider"></span>
          <span className="status-item">
            <i className="bi bi-shield-check"></i>
            <span>Secure Session</span>
          </span>
          <span className="status-divider"></span>
          <span className="status-item">
            <i className="bi bi-database-check"></i>
            <span>DB Connected</span>
          </span>
        </div>

        <div className="status-right">
          <span className="status-item shortcut-hint">
            <kbd>Ctrl+B</kbd> Bill &nbsp;
            <kbd>Ctrl+P</kbd> Payment &nbsp;
            <kbd>Ctrl+D</kbd> Dues &nbsp;
            <kbd>Ctrl+R</kbd> Summary
          </span>
          <span className="status-divider"></span>
          <span className="status-item version-tag">
            <i className="bi bi-calculator"></i> Billing v2.5
          </span>
        </div>
      </div>

      {/* Mobile Quick-Actions FAB + bottom sheet */}
      <button className="mobile-fab d-print-none" onClick={() => setMobilePanelOpen(true)} aria-label="Quick actions">
        <i className="bi bi-lightning-charge-fill"></i>
      </button>

      {mobilePanelOpen && (
        <div className="mobile-sheet-overlay" onClick={() => setMobilePanelOpen(false)}>
          <div className="mobile-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sheet-handle" onClick={() => setMobilePanelOpen(false)}></div>
            <div className="mobile-sheet-body" onClick={() => setMobilePanelOpen(false)}>
              <BillingQuickActions />
            </div>
          </div>
        </div>
      )}

      {showShortcutToast && (
        <div className="shortcut-toast">
          <i className="bi bi-keyboard"></i>
          <span>{showShortcutToast}</span>
        </div>
      )}

      <style>{`
        .billing-layout {
          --canvas-bg: #f1f5f9;
          --panel-bg: #ffffff;
          --panel-border: #e2e8f0;
          --accent: #d97706;
          --accent-dark: #b45309;
          --accent-soft: #fffbeb;

          display: flex; flex-direction: column;
          height: 100vh; background-color: var(--canvas-bg);
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          overflow: hidden;
        }

        .billing-main-area {
          display: flex; flex: 1; overflow: hidden;
        }

        .billing-content-wrapper {
          flex: 1; overflow-y: auto;
          padding: 14px 18px;
          background-color: var(--canvas-bg);
        }
        .billing-inner-canvas {
          background: transparent; min-height: 100%; border-radius: 8px;
        }

        .billing-content-wrapper::-webkit-scrollbar { width: 8px; height: 8px; }
        .billing-content-wrapper::-webkit-scrollbar-track { background: transparent; }
        .billing-content-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e1; border-radius: 10px;
          border: 2px solid var(--canvas-bg);
        }
        .billing-content-wrapper::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

        .billing-sidebar-right {
          width: 280px; background-color: var(--panel-bg);
          border-left: 1px solid var(--panel-border);
          display: flex; flex-direction: column;
          box-shadow: -4px 0 20px rgba(15, 23, 42, 0.04);
          z-index: 10; position: relative;
          transition: width 0.3s ease;
        }
        .billing-sidebar-right.collapsed { width: 56px; }

        .sidebar-toggle-btn {
          position: absolute; left: -12px; top: 16px;
          width: 24px; height: 24px; border-radius: 50%;
          background: white; border: 1px solid var(--panel-border);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; z-index: 11; color: #64748b;
          box-shadow: 0 2px 4px rgba(15, 23, 42, 0.08);
          transition: all 0.2s;
        }
        .sidebar-toggle-btn:hover {
          background: var(--accent); color: white; border-color: var(--accent);
        }

        .collapsed-icons {
          display: flex; flex-direction: column;
          gap: 8px; padding: 16px 8px; align-items: center;
        }
        .collapsed-icon-btn {
          width: 40px; height: 40px;
          background: #f8fafc; border: 1px solid #f1f5f9;
          border-radius: 9px; display: flex;
          align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.2s; font-size: 16px;
        }
        .collapsed-icon-btn:hover {
          background: white; transform: translateX(-2px);
          box-shadow: 0 2px 8px rgba(15, 23, 42, 0.1);
        }

        /* ===========================================================
           STATUS BAR — light theme, amber accents
           =========================================================== */
        .billing-statusbar {
          height: 30px;
          background: #ffffff;
          color: #475569; display: flex;
          align-items: center; justify-content: space-between;
          padding: 0 14px; font-size: 11px; font-weight: 600;
          border-top: 1px solid var(--panel-border);
          box-shadow: 0 -1px 3px rgba(15, 23, 42, 0.04);
          flex-shrink: 0;
        }
        .status-left, .status-right {
          display: flex; align-items: center; gap: 10px;
        }
        .status-item {
          display: flex; align-items: center; gap: 5px;
        }
        .status-item i { font-size: 11px; color: #94a3b8; }
        .status-online { color: #059669; font-weight: 700; }

        .status-dot-live {
          width: 7px; height: 7px;
          background: #10b981;
          border-radius: 50%;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25);
          animation: pulse-dot-live 2s ease infinite;
        }
        @keyframes pulse-dot-live {
          0%, 100% { box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.25); }
          50% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12); }
        }

        .status-divider {
          width: 1px; height: 14px; background: var(--panel-border);
        }

        .shortcut-hint kbd {
          background: #f1f5f9;
          color: #475569;
          padding: 1px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-family: 'Consolas', monospace;
          border: 1px solid var(--panel-border);
          margin: 0 2px;
        }

        .version-tag {
          color: var(--accent-dark);
          font-weight: 700;
          background: var(--accent-soft);
          padding: 2px 8px;
          border-radius: 5px;
          border: 1px solid #fde68a;
        }
        .version-tag i { color: var(--accent) !important; }

        .shortcut-toast {
          position: fixed; bottom: 50px; left: 50%;
          transform: translateX(-50%);
          background: #ffffff;
          color: #1e293b; padding: 11px 18px;
          border-radius: 24px; font-size: 13px;
          font-weight: 700; z-index: 9999;
          display: flex; align-items: center; gap: 8px;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.15);
          border: 1px solid #fde68a;
          animation: toastSlide 0.3s ease;
        }
        .shortcut-toast i { color: var(--accent); font-size: 15px; }
        @keyframes toastSlide {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }

        /* ===========================================================
           MOBILE FAB + BOTTOM SHEET (Quick Actions on phone)
           =========================================================== */
        .mobile-fab {
          display: none;
          position: fixed; right: 18px; bottom: 44px;
          width: 54px; height: 54px; border-radius: 50%;
          background: linear-gradient(135deg, #fbbf24 0%, #d97706 100%);
          color: white; border: none; font-size: 22px;
          align-items: center; justify-content: center;
          box-shadow: 0 6px 20px rgba(217, 119, 6, 0.45);
          cursor: pointer; z-index: 900;
          transition: transform 0.2s;
        }
        .mobile-fab:active { transform: scale(0.92); }
        .mobile-sheet-overlay {
          position: fixed; inset: 0;
          background: rgba(15, 23, 42, 0.45);
          backdrop-filter: blur(2px);
          z-index: 1500; display: flex; align-items: flex-end;
          animation: fadeIn 0.2s ease;
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .mobile-sheet {
          width: 100%; max-height: 80vh;
          background: #fff;
          border-radius: 18px 18px 0 0;
          display: flex; flex-direction: column;
          box-shadow: 0 -8px 30px rgba(15, 23, 42, 0.2);
          animation: sheetUp 0.28s ease;
          overflow: hidden;
        }
        @keyframes sheetUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .mobile-sheet-handle {
          width: 44px; height: 5px; border-radius: 3px;
          background: #cbd5e1; margin: 10px auto 4px;
          cursor: pointer; flex-shrink: 0;
        }
        .mobile-sheet-body { overflow-y: auto; flex: 1; }

        @media (max-width: 1024px) {
          .billing-sidebar-right { display: none; }
          .shortcut-hint { display: none; }
          .mobile-fab { display: flex; }
        }
        @media (max-width: 768px) {
          .status-left .status-item:nth-child(n+3) { display: none; }
          .status-left .status-divider { display: none; }
        }

        @media print {
          .d-print-none { display: none !important; }
          .billing-content-wrapper { padding: 0; background: white; }
          .billing-layout { background: white; height: auto; overflow: visible; }
          .billing-main-area { overflow: visible; height: auto; }
        }
      `}</style>
    </div>
  );
};

export default BillingLayout;